/**
 * PDP「商品説明」タブの折りたたみ（もっと見る / 閉じる）。
 *
 * 設計契約:
 * - 折りたたみは CSS 側が既定で当たっている（サーバー描画時点でクランプ済み）。
 *   「全部出してから畳む」順にすると長い説明文でガクッと縮むので、初期状態を畳んだ側に置く。
 * - JS の役割は測定だけ。内容がクランプ高を超えていなければ .is-clamped を外して
 *   ボタンとフェードを消す（短い説明文で無意味な「もっと見る」を出さない）。
 * - JS が動かない環境では section 側の <noscript> がクランプを解除する（本文が読めなくなるのを防ぐ）。
 * - パネルが hidden（非アクティブタブ）の間は高さが 0 なので測らない。ResizeObserver が
 *   可視化された時点で再度呼ぶ。
 */
(function () {
  'use strict';

  var EXPAND_LABEL = 'もっと見る';
  var COLLAPSE_LABEL = '閉じる';

  // 行の端数で「もっと見る」が出るのを防ぐ許容差（px）。
  // 1行ぶん(15px * 1.85 ≒ 28px)より小さく、端数(数px)より大きい値を採る。
  var TOLERANCE = 8;

  // transitionend が来ない場合（transition 無効・reduced motion 等）の保険（ms）
  var TRANSITION_FALLBACK = 500;

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function Collapsible(wrapper) {
    this.wrapper = wrapper;
    this.box = wrapper.querySelector('[data-spec-collapse]');
    this.content = wrapper.querySelector('[data-spec-collapse-content]');
    this.button = wrapper.querySelector('[data-spec-more]');
    this.label = wrapper.querySelector('[data-spec-more-label]');
    this.expanded = false;
    this.overflowing = false;
  }

  Collapsible.prototype.init = function () {
    if (!this.box || !this.content || !this.button || !this.label) return false;

    var self = this;
    this.wrapper.__specCollapse = this;

    this.button.addEventListener('click', function () {
      if (self.expanded) {
        self.collapse();
      } else {
        self.expand();
      }
    });

    this.measure();

    // 初回測定が済むまで transition を効かせない（短い説明文でフェードが
    // アニメーションしながら消えるチラつきを防ぐ）。次フレームで有効化する。
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        self.wrapper.classList.add('is-ready');
      });
    } else {
      this.wrapper.classList.add('is-ready');
    }

    // 画像の遅延読み込み・Webフォント適用・タブ切替での可視化・折返し幅の変化で
    // 本文の高さは後から変わる。ResizeObserver が使えるならそれで拾う。
    if (typeof window.ResizeObserver === 'function') {
      this.observer = new window.ResizeObserver(function () {
        self.measure();
      });
      this.observer.observe(this.content);
    }

    // vh 依存のクランプ高（min(..., 65vh)）はビューポート高でも変わる
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        self.measure();
      }, 150);
    });

    return true;
  };

  /**
   * 内容がクランプ高を超えているかを測り、ボタンとフェードの出し分けを決める。
   * 展開中は測らない（展開中は max-height を外しているので必ず「超えていない」に見えるため）。
   */
  Collapsible.prototype.measure = function () {
    if (this.expanded) return;

    // 非アクティブタブ（hidden）や display:none 配下では高さが取れない。測らずに次の機会へ譲る。
    if (!this.box.getClientRects().length) return;

    this.overflowing = this.box.scrollHeight - this.box.clientHeight > TOLERANCE;
    this.wrapper.classList.toggle('is-clamped', this.overflowing);
    this.button.hidden = !this.overflowing;
  };

  Collapsible.prototype.expand = function () {
    var self = this;
    this.expanded = true;
    this.wrapper.classList.add('is-expanded');
    this.button.setAttribute('aria-expanded', 'true');
    this.label.textContent = COLLAPSE_LABEL;

    // 実高さまで開いてから制限を外す。開き切ったあとに画像が読み込まれて
    // 高さが伸びても切れないよう、最終状態は max-height: none に落とす。
    this.box.style.maxHeight = this.box.scrollHeight + 'px';

    var settled = false;
    var settle = function (event) {
      if (event && event.target !== self.box) return;
      if (settled) return;
      settled = true;
      self.box.removeEventListener('transitionend', settle);
      if (self.expanded) self.box.style.maxHeight = 'none';
    };
    this.box.addEventListener('transitionend', settle);
    window.setTimeout(settle, TRANSITION_FALLBACK);
  };

  Collapsible.prototype.collapse = function () {
    var self = this;

    // max-height: none のままだと縮む側の transition が効かない。
    // 一度いまの実高さを px で固定してから、次フレームでクランプ高へ戻す。
    this.box.style.maxHeight = this.box.scrollHeight + 'px';
    void this.box.offsetHeight; // reflow を強制

    var finish = function () {
      self.expanded = false;
      self.wrapper.classList.remove('is-expanded');
      self.button.setAttribute('aria-expanded', 'false');
      self.label.textContent = EXPAND_LABEL;
      self.box.style.maxHeight = ''; // CSS のクランプ高へ戻す

      // 展開中に本文の奥までスクロールしていると、畳んだ瞬間に画面が飛ぶ。
      // 折りたたみ本体が画面上端より上に行っている時だけ先頭へ戻す。
      if (self.wrapper.getBoundingClientRect().top < 0) {
        self.wrapper.scrollIntoView({
          block: 'start',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(finish);
    } else {
      finish();
    }
  };

  function initAll(scope) {
    var root = scope || document;
    var wrappers = root.querySelectorAll('[data-spec-collapsible]');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      if (wrapper.dataset.specCollapseReady === '1') continue;
      var instance = new Collapsible(wrapper);
      if (instance.init()) wrapper.dataset.specCollapseReady = '1';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // テーマエディタでのセクション再描画
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // タブ切替で初めて表示された時に測り直す（ResizeObserver 非対応環境の保険）
  document.addEventListener('click', function (event) {
    if (!event.target || typeof event.target.closest !== 'function') return;
    if (!event.target.closest('.senchado-specifications__tab')) return;
    window.setTimeout(function () {
      var wrappers = document.querySelectorAll('[data-spec-collapsible]');
      for (var i = 0; i < wrappers.length; i++) {
        var w = wrappers[i];
        if (w.__specCollapse) w.__specCollapse.measure();
      }
    }, 0);
  });
})();
